/**
 * Sites, nodes and enrolment on the wire (PLAN §3, §8, §9.1, D10, D16, D21).
 *
 * A **site** is where a tenant's nodes run: an on-prem studio (`onprem`), or
 * the tenant's one automatic **cloud site** (`cloud`, id `cloud`), whose nodes
 * are the Fargate tasks of cloud productions (§9.1; they enrol in phase 6).
 * A **node** is one ACP container, enrolled into a site with a one-time code
 * (D21): the first becomes `main`, the second `backup`, and a third is refused
 * unless it enrols as a `witness` (D9's LAN witness).
 *
 * `.strict()` as the rest of the contract; every id is a key segment
 * (`entityIdWireSchema`), so none can forge a DynamoDB key.
 */
import { openEnum } from './openEnum.js';
import { z } from 'zod';
import { witnessInForceWireSchema } from './witness.js';
import { nodeReleaseWireSchema } from './nodeRelease.js';
import { entityIdWireSchema, isoTimestampWireSchema, nodeIdWireSchema, packageHashWireSchema, siteIdWireSchema, siteLinkStateWireSchema, studioIdWireSchema, tenantIdWireSchema } from './primitives.js';

/** The automatic cloud site's id — the same in every tenant (D10, §9.1). */
export const CLOUD_SITE_ID = 'cloud';

export const siteKindWireSchema = z.enum(['onprem', 'cloud']);
export type SiteKindWire = z.infer<typeof siteKindWireSchema>;

/** A node's role in its site (D9, D21). Per-production roles (active/standby) are the productions' own (§8.1). */
const SITE_NODE_ROLES = ['main', 'backup', 'witness'] as const;
/** Closed: what a request names and what a node is told it is (it cannot run as a role it does not know). */
export const siteNodeRoleWireSchema = z.enum(SITE_NODE_ROLES);
export type SiteNodeRoleWire = z.infer<typeof siteNodeRoleWireSchema>;
/** Open (D37 §4.3): another node's role, as a listing shows it — a newer cloud may know a role this reader does not. */
export const siteNodeRoleOpenWireSchema = openEnum(SITE_NODE_ROLES);

/** `revoked`: replaced (D21), its key refused everywhere from then on. Closed where the cloud itself holds it (its authorizer context). */
const NODE_STATUSES = ['active', 'revoked'] as const;
export const nodeStatusWireSchema = z.enum(NODE_STATUSES);
/** Open: a node's status as a listing shows it (the cloud refuses any node that is not `active`). */
const nodeStatusOpenWireSchema = openEnum(NODE_STATUSES);
export type NodeStatusWire = z.infer<typeof nodeStatusWireSchema>;

/** A raw 32-byte public key (Ed25519 or X25519), unpadded base64url: 43 characters. */
export const rawPublicKeyWireSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'a raw 32-byte public key, base64url (43 characters)');

/**
 * A node's LAN address as the janitor reports it (D16): IPv4 in a private
 * range only (RFC 1918, or 100.64/10 carrier-grade NAT), because the cloud
 * publishes it in public DNS under our zone with a public certificate for the
 * name — a public address there would lend our name and a valid certificate to
 * whatever answers on it.
 */
export const lanIpWireSchema = z.ipv4().refine(isPrivateIpv4, { message: 'the LAN IP must be a private IPv4 address (10/8, 172.16/12, 192.168/16 or 100.64/10)' });

export function isPrivateIpv4(ip: string): boolean {
  const parts = ip.split('.').map(Number);
  const [a, b] = parts;
  if (parts.length !== 4 || a === undefined || b === undefined) return false;
  return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) || (a === 100 && b >= 64 && b <= 127);
}

/**
 * Why a node's LAN IP is almost certainly NOT reachable from the studio's network, or null when it looks like a real
 * LAN address. With `--network host` a container sees its host's interfaces — but on Docker Desktop the "host" is
 * Docker's VM, whose addresses (192.168.65.0/24, and the default bridge 172.17.0.0/16) exist only inside it; on a Linux
 * host the default bridge `docker0` (172.17.0.1) is picked when nothing better is found. Link-local and loopback are
 * never another machine's way in. A VM's user-mode NAT adapter always gets the same address — 10.0.2.15 (VirtualBox,
 * QEMU/slirp) or 192.168.5.15 (Lima, Colima) — reachable from the VM's host at most, never from the LAN; the VM's
 * bridged or host-only adapter is the one to use. A main and a backup registered at such addresses each reach the cloud, but never
 * each other: their peer link stays down. Shared by the node (its log and `/health`) and the server list.
 */
export type LanIpProblem = 'docker-desktop' | 'docker-bridge' | 'vm-nat' | 'link-local' | 'loopback';

export function lanIpProblem(ip: string | null): LanIpProblem | null {
  if (ip === null) return null;
  const parts = ip.split('.').map(Number);
  const [a, b, c] = parts;
  if (parts.length !== 4 || a === undefined || b === undefined || c === undefined) return null;
  if (a === 192 && b === 168 && c === 65) return 'docker-desktop';
  if (a === 172 && b === 17) return 'docker-bridge';
  if (ip === '10.0.2.15' || ip === '192.168.5.15') return 'vm-nat';
  if (a === 169 && b === 254) return 'link-local';
  if (a === 127) return 'loopback';
  return null;
}

/** What an operator is told about a `lanIpProblem` address: why it is a problem, and the ways out. */
export function lanIpAdvice(ip: string, problem: LanIpProblem): string {
  const why =
    problem === 'docker-desktop' || problem === 'docker-bridge'
      ? `${ip} is a Docker-internal address (${problem === 'docker-desktop' ? 'Docker Desktop runs containers in a VM, so its host network is the VM’s' : 'the docker0 bridge, or Docker Desktop’s VM'})`
      : problem === 'vm-nat'
        ? `${ip} is a virtual machine's NAT adapter address (VirtualBox, QEMU, Lima/Colima), reachable at most from the VM's own host`
        : `${ip} is a ${problem} address`;
  return `this server’s LAN address isn’t reachable from your network: ${why}, so the other server of the pair cannot reach it. Use Docker Engine in WSL with mirrored networking, a Linux host, or a bridged VM — or, if the host has a LAN address the container can use, start it with -e ACP_LAN_IP=<that address>`;
}

const ARCHES = ['x86_64', 'arm64'] as const;
/** Closed: what a node enrols as (the cloud validates it). */
const archWireSchema = z.enum(ARCHES);
/** Open: an architecture as a listing shows it — a newer release may run on one this reader does not know. */
const archOpenWireSchema = openEnum(ARCHES);

/**
 * One IPv4 address a node sees on its host (`os.networkInterfaces()`, non-internal): a candidate for its LAN address,
 * as the node reports them in `hello` and `status` and the server list offers them. Carries no verdict of its own —
 * a reader judges each with `lanIpProblem` (so a newer diagnosis reaches the portal with the portal, not the node).
 */
export const lanCandidateWireSchema = z
  .object({
    /** The host's interface name (`eth0`, `enp3s0`, `docker0`). */
    interface: z.string().min(1).max(64),
    ip: z.ipv4(),
  })
  .strict();
export type LanCandidateWire = z.infer<typeof lanCandidateWireSchema>;

/** At most this many candidates are reported (a host with more interfaces reports the first, by name). */
export const MAX_LAN_CANDIDATES = 32;

/**
 * Where a node's LAN address comes from, as the node reports it: `environment` — `ACP_LAN_IP` on its container, which
 * wins over everything ("set on the server"); `cloud` — the address an admin chose in the portal (`updateNode`'s
 * `lanIp`); `detected` — the node's own pick among its candidates (`pickLanIp`). Open (D37): a newer node may add one.
 */
const LAN_IP_SOURCES = ['environment', 'cloud', 'detected'] as const;
export const lanIpSourceOpenWireSchema = openEnum(LAN_IP_SOURCES);
export type LanIpSourceWire = z.infer<typeof lanIpSourceOpenWireSchema>;

const siteNameSchema = z.string().trim().min(1).max(200);
const labelSchema = z.string().trim().min(1).max(100);

export const siteWireSchema = z
  .object({
    siteId: siteIdWireSchema,
    name: z.string(),
    kind: siteKindWireSchema,
    /** The studios that name this site (`siteId`), by id. */
    studios: z.array(studioIdWireSchema),
    createdAt: isoTimestampWireSchema,
    updatedAt: isoTimestampWireSchema,
  })
  .strict();
export type SiteWire = z.infer<typeof siteWireSchema>;

/** The last health report's gist (PLAN §11.1), or null before the first. The full reports are kept 7 days. */
const nodeHealthSummaryWireSchema = z
  .object({
    reportedAt: isoTimestampWireSchema,
    cpuPercent: z.number().min(0).max(100).nullable(),
    memoryUsedPercent: z.number().min(0).max(100).nullable(),
    diskUsedPercent: z.number().min(0).max(100).nullable(),
    clockDriftMs: z.number().nullable(),
    outboxDepth: z.number().int().nonnegative().nullable(),
    productions: z.number().int().nonnegative(),
    /** Which witness is in force on the node and its expected failover time, as its last report said (wave 4c). */
    witness: witnessInForceWireSchema.nullable().optional(),
  })
  .strict();

export const nodeWireSchema = z
  .object({
    nodeId: nodeIdWireSchema,
    siteId: siteIdWireSchema,
    role: siteNodeRoleOpenWireSchema,
    label: z.string(),
    lanIp: z.string().nullable(),
    arch: archOpenWireSchema,
    /** The node's Ed25519 public key, raw base64url: what verifies its signed requests. */
    publicKey: rawPublicKeyWireSchema,
    status: nodeStatusOpenWireSchema,
    enrolledAt: isoTimestampWireSchema,
    revokedAt: isoTimestampWireSchema.nullable(),
    /** When the cloud last heard from the node's site link, or null if never. */
    lastSeen: isoTimestampWireSchema.nullable(),
    linkState: siteLinkStateWireSchema,
    /** The packages the node reported holding (D15). */
    packageHashes: z.array(packageHashWireSchema),
    /** The node's local HTTPS name (D16), or null for a node without one (a cloud node, or no LAN IP yet). */
    dnsName: z.string().nullable(),
    /** When its current certificate expires, or null before the first is issued. */
    certExpiresAt: isoTimestampWireSchema.nullable(),
    health: nodeHealthSummaryWireSchema.nullable(),
    /**
     * The LAN address an admin chose for it in the portal (`updateNode`), or null: `auto` — the node picks its own.
     * `ACP_LAN_IP` on the server still wins (`lanIpSource: 'environment'`). Absent from an older cloud: read as auto.
     */
    lanIpSetting: lanIpWireSchema.nullable().optional(),
    /** The addresses the node reported seeing on its host (its last `hello`/`status`); absent before an up-to-date node reported. */
    lanCandidates: z.array(lanCandidateWireSchema).max(MAX_LAN_CANDIDATES).optional(),
    /** Where the node's current `lanIp` came from, as it reported; absent before an up-to-date node reported. */
    lanIpSource: lanIpSourceOpenWireSchema.optional(),
    /**
     * The release it runs, what it is served and its live productions' packages (`nodeRelease.ts`; D37). Absent from
     * an older cloud, and on a stack without a release store.
     */
    release: nodeReleaseWireSchema.optional(),
  })
  .strict();
export type SiteNodeWire = z.infer<typeof nodeWireSchema>;

// ─── requests and responses ───

export const sitePathWireSchema = z.object({ siteId: siteIdWireSchema.describe('The site’s id.') }).strict();
export const nodePathWireSchema = sitePathWireSchema.extend({ nodeId: nodeIdWireSchema.describe('The node’s id.') }).strict();

export const createSiteRequestWireSchema = z
  .object({
    /** A client-chosen id makes a retry idempotent. */
    siteId: entityIdWireSchema.refine((id) => id !== CLOUD_SITE_ID, { message: `"${CLOUD_SITE_ID}" is the automatic cloud site's id` }).optional(),
    name: siteNameSchema,
  })
  .strict();

export const updateSiteRequestWireSchema = z.object({ name: siteNameSchema }).strict();

/** Enrolment codes last at most 24 h (D21); the portal's default. */
const MAX_ENROLMENT_CODE_MINUTES = 24 * 60;

export const createEnrolmentCodeRequestWireSchema = z
  .object({
    /** How long the code is valid: 5 minutes to 24 hours (D21). */
    expiresInMinutes: z.number().int().min(5).max(MAX_ENROLMENT_CODE_MINUTES),
  })
  .strict();

/** `POST /sites/{siteId}/enrolment-codes`: optionally a witness code (D9, D21: a third node enrols only as the site's LAN witness). */
export const createSiteEnrolmentCodeRequestWireSchema = createEnrolmentCodeRequestWireSchema
  .extend({
    /** `witness`: the code enrols the site's LAN witness (its install line runs `--role witness`). Absent: the pairing order. */
    role: z.literal('witness').optional(),
  })
  .strict();

export const enrolmentCodeWireSchema = z
  .object({
    /**
     * The one-time code — `ACP1.<regional API URL, base64url>.<codeId>.<secret>` — shown once:
     * the cloud keeps only a hash of the secret.
     */
    code: z.string(),
    codeId: z.string(),
    siteId: siteIdWireSchema,
    expiresAt: isoTimestampWireSchema,
    /** A node enrolling with it takes this role (a replacement's), or null for the pairing order (D21). */
    role: siteNodeRoleOpenWireSchema.nullable(),
    /** The node it replaces, or null. */
    replacesNodeId: nodeIdWireSchema.nullable(),
    /** The install line to run on the node's host (D21). */
    dockerRun: z.string(),
  })
  .strict();
export type EnrolmentCodeWire = z.infer<typeof enrolmentCodeWireSchema>;

/** An enrolment code's id: a ULID (`@acp/cloud-link/enrolmentCode.js`). */
const enrolmentCodeIdWireSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'enrolment code ids are ULIDs');

/**
 * A code issued for the site and not used yet (unspent, unexpired, not cancelled, not dead of wrong secrets): the
 * portal's "waiting for the server to connect…" row. Never the secret — the cloud keeps only its hash, and the code
 * itself was shown once, when it was issued. A cloud production task's credential is never listed.
 */
export const pendingEnrolmentWireSchema = z
  .object({
    codeId: enrolmentCodeIdWireSchema,
    siteId: siteIdWireSchema,
    /** The role the node enrolling with it takes (a replacement's, a witness code's), or null for the pairing order. */
    role: siteNodeRoleOpenWireSchema.nullable(),
    /** The node it replaces, or null. */
    replacesNodeId: nodeIdWireSchema.nullable(),
    createdAt: isoTimestampWireSchema,
    expiresAt: isoTimestampWireSchema,
    /** The user who issued it. */
    createdBy: z.string(),
  })
  .strict();
export type PendingEnrolmentWire = z.infer<typeof pendingEnrolmentWireSchema>;

export const enrolmentCodePathWireSchema = sitePathWireSchema.extend({ codeId: enrolmentCodeIdWireSchema.describe('The enrolment code’s id.') }).strict();

/** `POST /sites/{siteId}/nodes/{nodeId}/remove`. */
export const removeNodeRequestWireSchema = z
  .object({
    /**
     * Remove it even though its last health report says it runs productions (active or standby), or it is the
     * site's last main/backup while a production of a studio on the site is active. Those productions lose this
     * server at once: the portal asks for the node's name to be typed first.
     */
    force: z.boolean().optional(),
  })
  .strict();

/** `PATCH /sites/{siteId}/nodes/{nodeId}`: rename it, and/or choose its LAN address — each optional, at least one. */
export const updateNodeRequestWireSchema = z
  .object({
    /** What the portal and the CLI call it (its host's name, by default: the install line's `ACP_NODE_LABEL`). */
    label: labelSchema.optional(),
    /**
     * Its LAN address — the one its DNS name points at and its pair dials (D16): one it reported (`lanCandidates`) or
     * any private IPv4 (a NATed host's). Null: `auto`, the node's own pick. `ACP_LAN_IP` on the server wins over both.
     */
    lanIp: lanIpWireSchema.nullable().optional(),
  })
  .strict()
  .refine((body) => body.label !== undefined || body.lanIp !== undefined, { message: 'name a label, a lanIp, or both' });
export type UpdateNodeRequestWire = z.infer<typeof updateNodeRequestWireSchema>;

export const swapRolesRequestWireSchema = z
  .object({
    /** The node that becomes main; the current main becomes backup. Naming the target makes a retry idempotent. */
    mainNodeId: nodeIdWireSchema,
  })
  .strict();

export const siteResponseWireSchema = z.object({ site: siteWireSchema }).strict();
export const listSitesResponseWireSchema = z.object({ sites: z.array(siteWireSchema) }).strict();
export const listNodesResponseWireSchema = z.object({ nodes: z.array(nodeWireSchema) }).strict();
export const enrolmentCodeResponseWireSchema = z.object({ enrolment: enrolmentCodeWireSchema }).strict();
export const replaceNodeResponseWireSchema = z.object({ node: nodeWireSchema, enrolment: enrolmentCodeWireSchema }).strict();
export const listPendingEnrolmentsResponseWireSchema = z.object({ enrolments: z.array(pendingEnrolmentWireSchema) }).strict();
export const pendingEnrolmentResponseWireSchema = z.object({ enrolment: pendingEnrolmentWireSchema }).strict();
export const removeNodeResponseWireSchema = z
  .object({
    node: nodeWireSchema,
    /** The backup made main because the main was removed (the site keeps a main), or null. */
    promotedNodeId: nodeIdWireSchema.nullable(),
  })
  .strict();
export const nodeResponseWireSchema = z.object({ node: nodeWireSchema }).strict();

// ─── the enrolment exchange (D21), unauthenticated but code-gated ───

export const enrolRequestWireSchema = z
  .object({
    code: z.string().min(1).max(512),
    /** Ed25519, raw base64url: signs the node's requests from now on. */
    nodePublicKey: rawPublicKeyWireSchema,
    /** X25519, raw base64url: what the cloud seals the node's TLS key and the site key to. */
    encryptionPublicKey: rawPublicKeyWireSchema,
    label: labelSchema,
    lanIp: lanIpWireSchema,
    arch: archWireSchema,
    /** `witness` for a LAN witness (`--role witness`, D9); omitted for the pairing order. */
    role: z.literal('witness').optional(),
  })
  .strict();
export type EnrolRequestWire = z.infer<typeof enrolRequestWireSchema>;

/** A box sealed to a node's X25519 key (`@acp/cloud-link/seal.js`): X25519 → HKDF-SHA256 → AES-256-GCM. */
export const sealedBoxWireSchema = z
  .object({
    alg: z.literal('X25519-HKDF-SHA256-A256GCM'),
    /** The ephemeral public key, raw base64url. */
    epk: rawPublicKeyWireSchema,
    nonce: z.string().regex(/^[A-Za-z0-9_-]{16}$/, 'a 96-bit nonce, base64url'),
    /** Ciphertext then the 16-byte GCM tag, base64url. */
    ct: z.string().regex(/^[A-Za-z0-9_-]+$/).max(64 * 1024),
  })
  .strict();
export type SealedBoxWire = z.infer<typeof sealedBoxWireSchema>;

/**
 * The site's signing key (D20): an Ed25519 key shared by the site's nodes,
 * which sign local session tokens with it so either node accepts the other's.
 * The private half is sealed to the receiving node (context
 * `site-key:<siteId>:<nodeId>`).
 */
export const siteKeyDeliveryWireSchema = z
  .object({
    /** Changes when the key is rotated; a node reports the one it holds. */
    keyId: z.string().min(1).max(64),
    publicKey: rawPublicKeyWireSchema,
    sealedPrivateKey: sealedBoxWireSchema,
  })
  .strict();

export const enrolResponseWireSchema = z
  .object({
    tenantId: tenantIdWireSchema,
    siteId: siteIdWireSchema,
    nodeId: nodeIdWireSchema,
    role: siteNodeRoleWireSchema,
    /** Where this regional stack answers (D12): the REST API and the site link's WebSocket. */
    stack: z.object({ apiUrl: z.url(), wsUrl: z.url() }).strict(),
    /** What a node needs to verify Cognito tokens offline (D5, D20). */
    cognito: z.object({ issuer: z.url(), jwksUrl: z.url(), clientIds: z.array(z.string()) }).strict(),
    siteKey: siteKeyDeliveryWireSchema,
    /** The node's local HTTPS name and the site alias (D16), or null (a cloud node). The certificate follows over the site link. */
    dns: z.object({ nodeName: z.string(), siteName: z.string() }).strict().nullable(),
  })
  .strict();
export type EnrolResponseWire = z.infer<typeof enrolResponseWireSchema>;

/** `GET /node/self`: the calling node as the cloud has it — the simplest signed call. */
export const nodeSelfResponseWireSchema = z.object({ tenantId: tenantIdWireSchema, node: nodeWireSchema }).strict();
